'use-strict';

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';
import Photoswipe from './photoswipe.esm.min.js';

class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || 'open_lightbox';
    this.zoomOption = this.dataset.zoomOption || 'external';
    this.lightbox = null;
    this.drift = null;
    this._resizeHandler = this.responsive.bind(this);
    this.init();
  }

  init() {
    window.addEventListener('resize', this._resizeHandler);
    window.addEventListener('load', this._resizeHandler);
  }

  isDriftMode() {
    const driftOptions = [
      'external', 'external_zoom',
      'inner-1', 'inner-2',
      'inner_zoom_circle', 'inner_zoom_square'
    ];
    return driftOptions.includes(this.zoomOption);
  }

  initLightBox() {
    if (this.lightbox !== null) return;

    this.querySelectorAll('a.media-gallery__image').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();

        let position = 0;
        const target = e.currentTarget;

        if (target.getAttribute('data-position')) {
          position = parseInt(target.getAttribute('data-position'), 10);
        } else if (target.closest('media-gallery')?.querySelector('.swiper-slide-active')) {
          position = parseInt(
            target.closest('media-gallery')
              .querySelector('.swiper-slide-active')
              .getAttribute('data-position'),
            10
          );
        } else if (target.closest('media-gallery')?.firstElementChild) {
          position = parseInt(
            target.closest('media-gallery')
              .firstElementChild
              .getAttribute('data-position'),
            10
          );
        }

        if (this.lightbox !== null) {
          this.lightbox.destroy();
          this.lightbox = null;
        }

        this.lightbox = new PhotoSwipeLightbox({
          gallery: this,
          children: 'a',
          pswpModule: () => Photoswipe,
          counter: false,
          zoom: false,
          preloader: false,
          arrowPrev: false,
          arrowNext: false,
          close: false,
          loop: true,
          bgOpacity: 1,
        });

        this.lightbox.on('uiRegister', () => {
          const { pswp } = this.lightbox;

          pswp?.ui.registerElement({
            name: 'bls--close',
            isButton: true,
            tagName: 'button',
            html: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="transition"><use href="#icon-close"></use></svg>',
            onClick: () => pswp.close(),
          });

          pswp?.ui.registerElement({
            name: 'bottomBar',
            className: 'pswp__bottom-bar',
            appendTo: 'wrapper',
            onInit: (el, pswp) => {
              const next = document.createElement('button');
              next.setAttribute('type', 'button');
              next.className = 'pswp__button pswp__button-next';
              next.innerHTML = `<svg width="6" height="11" fill="none"><use href="#icon-next"></use></svg>`;
              next.onclick = () => pswp.next();
              el.appendChild(next);

              const counter = document.createElement('span');
              counter.className = 'pswp__counter';
              pswp.on('change', () => {
                counter.innerText = pswp.currIndex + 1 + pswp.options.indexIndicatorSep + pswp.getNumItems();
              });
              el.appendChild(counter);

              const prev = document.createElement('button');
              prev.setAttribute('type', 'button');
              prev.className = 'pswp__button pswp__button-prev';
              prev.innerHTML = `<svg width="6" height="11" fill="none"><use href="#icon-back"></use></svg>`;
              prev.onclick = () => pswp.prev();
              el.appendChild(prev);
            },
          });
        });

        this.lightbox.init();
        this.lightbox.loadAndOpen(position - 1);
      });
    });
  }

  initDrift() {
    if (this.drift !== null) return;

    if (typeof Drift === 'undefined') {
      console.warn('Drift.js is not loaded');
      return;
    }

    const allTriggers = this.querySelectorAll('.drift-trigger');
    if (allTriggers.length === 0) {
      console.warn('No .drift-trigger elements found inside zoom-action');
      return;
    }

    const featuredProduct = this.closest('.sec__featured-product');
    const paneContainer = featuredProduct
      ? featuredProduct.querySelector('.zoom-external-area')
      : null;

    const zoomOption = this.zoomOption;

    allTriggers.forEach((trigger) => {
      const inlineContainer = trigger.closest('[data-pane-container]');

      if (!inlineContainer) {
        console.warn('No [data-pane-container] found for trigger', trigger);
        return;
      }

      if (zoomOption === 'inner-1' || zoomOption === 'inner_zoom_circle' || zoomOption === 'inner_zoom_square') {
        this.drift = new Drift(trigger, {
          inlinePane: true,
          zoomFactor: 4,
          containInline: false,
          paneContainer: inlineContainer,
          hoverBoundingBox: false,
          onShow: () => inlineContainer.classList.add('relative'),
          onHide: () => inlineContainer.classList.remove('relative'),
        });

      } else if (zoomOption === 'inner-2') {
        this.drift = new Drift(trigger, {
          inlinePane: true,
          zoomFactor: 3,
          containInline: true,
          paneContainer: inlineContainer,
          hoverBoundingBox: false,
          onShow: () => inlineContainer.classList.add('relative'),
          onHide: () => inlineContainer.classList.remove('relative'),
        });

      } else if (zoomOption === 'external' || zoomOption === 'external_zoom') {
        this.drift = new Drift(trigger, {
          inlinePane: false,
          zoomFactor: 5,
          containInline: false,
          paneContainer: paneContainer || inlineContainer,
          hoverBoundingBox: true,
          onShow: () => inlineContainer.classList.add('relative'),
          onHide: () => inlineContainer.classList.remove('relative'),
        });

      } else {
        // fallback for any other inner zoom value
        this.drift = new Drift(trigger, {
          inlinePane: true,
          zoomFactor: 4,
          containInline: false,
          paneContainer: inlineContainer,
          hoverBoundingBox: false,
          onShow: () => inlineContainer.classList.add('relative'),
          onHide: () => inlineContainer.classList.remove('relative'),
        });
      }
    });
  }

  destroyLightbox() {
    if (this.lightbox !== null) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }

  destroyDrift() {
    if (this.drift !== null) {
      this.drift.destroy();
      this.drift = null;
    }
  }

  responsive() {
    if (window.innerWidth >= 768) {
      if (this.isDriftMode()) {
        this.destroyLightbox();
        this.initDrift();
      } else {
        this.destroyDrift();
        this.initLightBox();
      }
    } else {
      this.destroyDrift();
      this.destroyLightbox();
      this.initLightBox();
    }
    this.updateSwiper();
  }

  updateSwiper() {
    if (typeof Swiper !== 'undefined') {
      const thumbnailSlide = this.querySelector('.thumbnail-slide');
      if (thumbnailSlide?.swiper) {
        thumbnailSlide.swiper.update();
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._resizeHandler);
    window.removeEventListener('load', this._resizeHandler);
    this.destroyLightbox();
    this.destroyDrift();
  }
}

customElements.define('zoom-action', ZoomAction);